import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Radio, Space, Upload } from 'antd';
import { LinkOutlined, PictureOutlined } from '@ant-design/icons';

type MailHtmlEditorProps = {
    value: string;
    onChange: (value: string) => void;
    onUploadImage: (file: File) => Promise<string>;
    disabled?: boolean;
};

const EMPTY_DOCUMENT = '<!doctype html><html><head></head><body><p><br></p></body></html>';

const MailHtmlEditor: React.FC<MailHtmlEditorProps> = ({
    value,
    onChange,
    onUploadImage,
    disabled,
}) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const selectionRef = useRef<Range | null>(null);
    const lastEmittedRef = useRef(value);
    const [mode, setMode] = useState<'visual' | 'source'>('visual');
    const [frameSource, setFrameSource] = useState(value || EMPTY_DOCUMENT);
    const [uploadingImage, setUploadingImage] = useState(false);

    useEffect(() => {
        if (value !== lastEmittedRef.current) {
            lastEmittedRef.current = value;
            setFrameSource(value || EMPTY_DOCUMENT);
        }
    }, [value]);

    const editorDocument = () => iframeRef.current && iframeRef.current.contentDocument;

    const emitDocument = () => {
        const doc = editorDocument();
        if (!doc || !doc.documentElement) return;
        const html = `<!doctype html>${doc.documentElement.outerHTML}`;
        lastEmittedRef.current = html;
        onChange(html);
    };

    const rememberSelection = () => {
        const doc = editorDocument();
        const selection = doc && doc.getSelection();
        if (!doc || !selection || selection.rangeCount === 0) return;
        const range = selection.getRangeAt(0);
        const node = range.commonAncestorContainer.nodeType === Node.TEXT_NODE
            ? range.commonAncestorContainer.parentNode
            : range.commonAncestorContainer;
        if (node && doc.body.contains(node)) selectionRef.current = range.cloneRange();
    };

    const restoreSelection = () => {
        const doc = editorDocument();
        const selection = doc && doc.getSelection();
        if (!doc || !selection) return;
        doc.body.focus();
        if (selectionRef.current) {
            selection.removeAllRanges();
            selection.addRange(selectionRef.current);
        }
    };

    const execute = (command: string, argument?: string) => {
        const doc = editorDocument();
        if (!doc || disabled) return;
        restoreSelection();
        doc.execCommand(command, false, argument);
        rememberSelection();
        emitDocument();
    };

    const onFrameLoad = () => {
        const doc = editorDocument();
        if (!doc) return;
        doc.designMode = disabled ? 'off' : 'on';
        doc.body.setAttribute('contenteditable', disabled ? 'false' : 'true');
        doc.addEventListener('input', emitDocument);
        doc.addEventListener('keyup', rememberSelection);
        doc.addEventListener('mouseup', rememberSelection);
        doc.addEventListener('selectionchange', rememberSelection);
    };

    const addLink = () => {
        const url = window.prompt('请输入完整的 HTTPS 链接');
        if (url) execute('createLink', url.trim());
    };

    const insertUploadedImage = async (file: File) => {
        setUploadingImage(true);
        try {
            const url = await onUploadImage(file);
            const doc = editorDocument();
            if (!doc) return;
            restoreSelection();
            const safeUrl = url.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
            doc.execCommand(
                'insertHTML',
                false,
                `<img src="${safeUrl}" alt="邮件图片" style="max-width:100%;height:auto;">`
            );
            rememberSelection();
            emitDocument();
        } finally {
            setUploadingImage(false);
        }
        return false;
    };

    const switchMode = (nextMode: 'visual' | 'source') => {
        if (nextMode === 'visual') setFrameSource(value || EMPTY_DOCUMENT);
        setMode(nextMode);
    };

    return (
        <div className="mail-html-editor">
            <div className="mail-html-editor__mode">
                <Radio.Group value={mode} onChange={(event) => switchMode(event.target.value)}>
                    <Radio.Button value="visual">富文本编辑</Radio.Button>
                    <Radio.Button value="source">HTML 源码</Radio.Button>
                </Radio.Group>
            </div>
            {mode === 'visual' ? (
                <>
                    <Space className="mail-html-editor__toolbar">
                        <Button size="small" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => execute('bold')}>
                            加粗
                        </Button>
                        <Button size="small" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => execute('italic')}>
                            斜体
                        </Button>
                        <Button size="small" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => execute('underline')}>
                            下划线
                        </Button>
                        <Button size="small" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => execute('formatBlock', '<h2>')}>
                            标题
                        </Button>
                        <Button size="small" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => execute('insertUnorderedList')}>
                            项目符号
                        </Button>
                        <Button size="small" icon={<LinkOutlined />} disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={addLink}>
                            插入链接
                        </Button>
                        <Upload
                            accept="image/png,image/jpeg,image/gif"
                            showUploadList={false}
                            beforeUpload={(file) => {
                                insertUploadedImage(file as File).catch(() => undefined);
                                return false;
                            }}
                        >
                            <Button size="small" icon={<PictureOutlined />} loading={uploadingImage} disabled={disabled}>
                                上传并插入图片
                            </Button>
                        </Upload>
                        <Button size="small" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => execute('removeFormat')}>
                            清除格式
                        </Button>
                    </Space>
                    <iframe
                        ref={iframeRef}
                        title="邮件富文本编辑器"
                        sandbox="allow-same-origin"
                        srcDoc={frameSource}
                        onLoad={onFrameLoad}
                    />
                </>
            ) : (
                <Input.TextArea
                    value={value}
                    disabled={disabled}
                    rows={20}
                    spellCheck={false}
                    onChange={(event) => {
                        lastEmittedRef.current = event.target.value;
                        onChange(event.target.value);
                    }}
                />
            )}
        </div>
    );
};

export default MailHtmlEditor;
